<template>
  <div class="status-card" :class="{ clickable, grouped, 'has-items': hasItems }">
    <div
      class="icon"
      :style="{ fontSize: '24px', color: props.color, backgroundColor: props.color + '20' }"
    >
      <HddOutlined v-if="normalizedIconType === 'HddOutlined'" />
      <InfoCircleFilled v-else-if="normalizedIconType === 'InfoCircleFilled'" />
      <WarningFilled v-else-if="normalizedIconType === 'WarningFilled'" />
      <CheckCircleFilled v-else-if="normalizedIconType === 'CheckCircleFilled'" />
      <ClockCircleFilled v-else-if="normalizedIconType === 'ClockCircleFilled'" />
      <ExclamationCircleFilled v-else-if="normalizedIconType === 'ExclamationCircleFilled'" />
    </div>
    <div class="content">
      <template v-if="grouped">
        <div class="title">{{ props.title }}</div>
        <div v-if="hasItems" class="items">
          <div v-for="item in props.items" :key="item.label" class="item">
            <span class="item-label">{{ item.label }}</span>
            <span class="item-count" :style="{ color: item.color || props.color }">{{
              item.count
            }}</span>
          </div>
        </div>
        <div v-else class="count" :style="{ color: props.color }">{{ props.count }}</div>
      </template>
      <template v-else>
        <div class="count" :style="{ color: props.color }">{{ props.count }}</div>
        <div class="title">{{ props.title }}</div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  HddOutlined,
  InfoCircleFilled,
  WarningFilled,
  CheckCircleFilled,
  ClockCircleFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons-vue'

defineOptions({
  name: 'StatusCard',
})

export interface StatusCardItem {
  label: string
  count: number
  color?: string
}

interface Props {
  title: string
  count?: number
  iconType?: string
  color?: string
  grouped?: boolean
  clickable?: boolean
  items?: StatusCardItem[]
}

const props = withDefaults(defineProps<Props>(), {
  iconType: 'HddOutlined',
  color: '#1890ff',
  title: '设备总数',
  count: 0,
  grouped: false,
  clickable: true,
  items: () => [],
})

const hasItems = computed(() => props.items != null && props.items.length > 0)

const normalizedIconType = computed(() => {
  const iconType = props.iconType
  if (!iconType) return 'HddOutlined'
  if (iconType.charAt(0) === iconType.charAt(0).toUpperCase()) {
    return iconType
  }
  return iconType.charAt(0).toUpperCase() + iconType.slice(1)
})
</script>

<style scoped lang="less">
.status-card {
  display: flex;
  align-items: center;
  border: 1px solid #e8e8e8;
  padding: 20px;
  flex-grow: 1;
  background-color: #fff;
  border-radius: 8px;

  &.clickable:hover {
    border-color: #1890ff;
    cursor: pointer;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  }

  &.grouped {
    align-items: flex-start;
    .content {
      flex: 1;
    }
    .title {
      font-size: 14px;
      color: #666;
      margin-bottom: 10px;
    }
  }

  &.grouped.has-items {
    .title {
      text-align: left;
      font-weight: 600;
      color: #1f1f1f;
      font-size: 16px;
    }
  }
}
.icon {
  font-size: 24px;
  padding: 12px;
  border-radius: 8px;
}
.content {
  margin-left: 16px;
}
.title {
  font-size: 12px;
  color: #999;
}
.count {
  font-size: 24px;
  font-weight: bold;
  line-height: 1.2;
}
.items {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
}
.item {
  display: flex;
  flex-direction: column;
  min-width: 56px;
}
.item-label {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}
.item-count {
  font-size: 20px;
  font-weight: bold;
  line-height: 1.2;
}
</style>
